import type { CovBannerItem } from "@cx-component-library/react-components";
import { CovBannerCarousel } from "@cx-component-library/react-components";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import type { ImageCarouselAttribute } from "components/carousels/bgImagesCarousel";
import type { CardCarouselAttribute } from "components/carousels/CardsCarousel";
import { SynchronizedCarousels } from "components/carousels/SynchronizedCarousels";
import { ContactRequestButton } from "components/commons/ContactRequestButton";
import { SearchButtonAuthen } from "components/commons/SearchButtonAuthen";
import { UnauthenticationDialog } from "components/dialog/UnauthenticationDialog";
import { DiscoverySection } from "components/discovery";
import {
  readHandoffFile,
  withoutHandoffFile,
} from "components/discovery/explore-image/upload/uploadHandoff";
import { SearchFilter } from "components/filter/SearchFilter";
import { FullScreenLoader } from "components/loader/FullScreenLoader";
import { useAuthUser } from "hooks/authentication";
import { useColoredMaterials } from "hooks/colored-materials";
import type { FilterEntry } from "hooks/filters";
import { useLandingPage } from "hooks/landing-page";
import { useFeatureFlags } from "hooks/use-config";
import { useEffectEvent } from "hooks/utils/effect-event";
import type { CdcUserAttributes } from "models/cdcUser";
import { DiscoveryNavOutlet } from "providers/DiscoveryNavProvider";
import { useNavigationGuard } from "providers/NavigationGuardProvider";
import { SettingsContext } from "providers/SettingsProvider";
import { useUserInfoContext } from "providers/UserInfoProvider";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getDataFromLocalstorageByKey } from "service/utils/get-data-localstorage";
import { canUseConventionalSearch } from "service/utils/get-user-type";
import { MaterialShowcase } from "./MaterialShowcaseContainer";
import "./StartPage.css";
import { PromotionalCTA } from "./PromotionalCTA";
import { TrendCarousel } from "./TrendCarousel";
import { useTrendCarouselItems } from "./useTrendCarouselItems";

export const defaultFilters: Array<FilterEntry> = [];

/** Reads the discovery tab requested via router state (CX-3737), if any. */
function readRequestedTab(state: unknown): string | null {
  if (!state || typeof state !== "object" || !("requestedTab" in state)) {
    return null;
  }
  return (state as { requestedTab: string | null }).requestedTab;
}

/** Convert landing-page HTML longDescription to plain text for CovBannerCarousel subtitleDetails.
 *  Uses DOMParser for safe HTML parsing; preserves line breaks and list bullets. */
function htmlToPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");

  function nodeToText(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent ?? "";
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return "";
    }
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    const children = Array.from(el.childNodes).map(nodeToText).join("");
    if (tag === "li") return `• ${children}\n`;
    if (tag === "br") return "\n";
    if (tag === "p" || tag === "div") return `${children}\n`;
    return children;
  }

  return nodeToText(doc.body)
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: pre-existing top-level page component; the CX-3737 change adds a readRequestedTab() call and an initialTabId prop. A full decomposition is out of scope for this fix.
export const StartPage = ({ enableListView }: { enableListView?: boolean }) => {
  const [filters, setFilters] = useState(defaultFilters);
  const [openAuthDialog, setOpenAuthDialog] = useState(false);
  const { settings } = useContext(SettingsContext);
  const flags = useFeatureFlags();

  // Captured once on mount: a discovery nav tab selected while on another
  // discovery page (e.g. SearchPage) navigates back here with the target
  // tab in router state (CX-3737).
  const location = useLocation();
  const { state } = location;
  const [requestedTab] = useState<string | null>(() => readRequestedTab(state));
  // Image dropped on the /search sidebar (CX-3746), captured once like the tab.
  const [initialImageFile] = useState<File | null>(() =>
    readHandoffFile(state),
  );

  const isHomepageV2 = flags.homepageV2.enabled;
  const uploadMatchEnabled = enableListView ?? flags.uploadMatch;

  const cdcUser: CdcUserAttributes | undefined =
    getDataFromLocalstorageByKey("cdcUser");
  const { user } = useAuthUser();
  const { userInfo } = useUserInfoContext();
  const navigate = useNavigate();
  const { requestNavigation } = useNavigationGuard();

  // The file now lives in component state; drop it from history so a reload
  // or Back doesn't restart the Upload & Match flow.
  const clearHandoffFile = useEffectEvent(() =>
    navigate(location, { replace: true, state: withoutHandoffFile(state) }),
  );
  useEffect(() => {
    if (initialImageFile) {
      clearHandoffFile();
    }
  }, [initialImageFile, clearHandoffFile]);
  // Hold the file back until the history replace has committed. The router
  // applies it as a low-priority transition, and its new location.key makes
  // ExploreImageFlow discard any flow already started — so loading the image
  // earlier could be wiped by that late commit.
  const handoffCleared = readHandoffFile(state) === null;

  const isLogin = Boolean(user?.email) || Boolean(cdcUser);
  const showConventionalSearch = canUseConventionalSearch(user, settings);

  const [result, error, isLoading] = useColoredMaterials(
    filters,
    undefined,
    0,
    0,
    !isHomepageV2 || isLogin,
  );
  const [landingPageData, , landingPageLoading] = useLandingPage(
    settings.language.toLowerCase(),
  );

  const { items: trendItems } = useTrendCarouselItems();

  // V2: CovBannerCarousel data mapping. Excludes the Customer Lounge slide
  // (CX-2978) — that feature isn't ready for the V2 banner yet, per the same
  // reasoning CX-2283 hid the whole section for. The live CMS content labels
  // this slide "COLLABORATION" with a link.url pointing at the
  // customer-lounge subdomain, so filter on the destination host rather than
  // the label text (which can be relabeled without code changing).
  const banners: CovBannerItem[] = useMemo(() => {
    if (!isHomepageV2 || !landingPageData?.data?.length) {
      return [];
    }
    return landingPageData.data
      .filter((item) => !item.link.url.includes("customer-lounge"))
      .map((item) => ({
        id: item.carouselOrder.toString(),
        backgroundImageUrl: item.teaserImage,
        actionButton: {
          label: item.teaserLabelText,
          route: "/search",
        },
        title: item.title,
        subtitle: item.shortDescription,
        subtitleDetails: item.longDescription
          ? htmlToPlainText(item.longDescription)
          : undefined,
        readMoreLink: item.longDescription
          ? { label: "Read more", route: "" }
          : undefined,
        // External links (OMD, VRED) always show. An internal route (e.g.
        // /search) is shown only while logged out — it exists to prompt
        // registration, so it's redundant (and hidden) once the user is in.
        learnMoreLink:
          item.link.text && (item.link.url.startsWith("http") || !isLogin)
            ? { label: item.link.text, route: item.link.url }
            : undefined,
      }));
  }, [landingPageData, isHomepageV2, isLogin]);

  // SynchronizedCarousels (V1) data mapping — also reused by V2 per CX-2283
  const ldPageCard: CardCarouselAttribute[] = useMemo(() => {
    if (!landingPageData?.data?.length) {
      return [];
    }
    return landingPageData.data.map((i) => ({
      key: i.carouselOrder.toString(),
      boxName: i.teaserLabelText,
      bgImage: i.teaserImage,
      boxColorBg: i.teaserLabelColor,
      boxContentTitle: i.title,
      boxContentText: i.shortDescription,
      btnActionName: i.link.text,
      urlAction: i.link.url,
    }));
  }, [landingPageData]);

  const bannerPageCard: ImageCarouselAttribute[] = useMemo(() => {
    if (!landingPageData?.data?.length) {
      return [];
    }
    return landingPageData.data.map((i) => ({
      src: i.teaserImage,
      label: i.teaserLabelText,
      title: i.title,
      text: i.shortDescription,
      textMore: i.longDescription,
      bgColor: i.teaserLabelColor,
    }));
  }, [landingPageData]);

  const handleNavigate = (route: string) => {
    if (!route) return;
    if (route.startsWith("http")) {
      window.open(route, "_blank", "noopener,noreferrer");
      return;
    }
    if (!isLogin) {
      setOpenAuthDialog(true);
      return;
    }
    requestNavigation(() => navigate(route));
  };

  const [flowStep, setFlowStep] = useState("idle");
  const handleFlowStepChange = useCallback((step: string) => {
    setFlowStep(step);
  }, []);
  const showResultsOnly =
    flowStep === "region-selection" ||
    flowStep === "analysis" ||
    flowStep === "results";

  const { t } = useTranslation();

  const theme = useTheme();

  // Raw useMediaQuery here: use-breakpoint.ts only exposes mobile/tablet/desktop
  // (sm/lg) helpers. The search-filter grid column count depends on the `md` and
  // `xl` breakpoints, neither of which has a project hook — and `xl` is not used
  // anywhere else in the webapp, so adding a one-off helper would be premature.
  const isXl = useMediaQuery(theme.breakpoints.up("xl"));
  const isMd = useMediaQuery(theme.breakpoints.up("md"));

  const columns = isXl ? 4 : isMd ? 2 : 1;
  const childColumns = 12 / columns;

  const setAndSaveFilters = (newFilters: FilterEntry[]) => {
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: pre-existing color-system filter logic, unchanged and unrelated to the CX-3737 discovery-nav fix. Decomposing it is its own ticket.
    setFilters((prevFilters) => {
      const filterColor = newFilters.find((i) => i.type === "color");
      const isColorSystemIndex = newFilters.findIndex(
        (i) => i.type === "isColorSystem",
      );
      const colorSystemCodeIndex = newFilters.findIndex(
        (i) => i.type === "colorSystemCode",
      );

      // Auto-tick the checkbox for supported color systems (only when not explicitly unchecked by user)
      const supportedColorSystems = ["ncs", "ral_classic", "ral_design_plus"];
      const selectedColorSystem = filterColor?.value?.colorSystem;
      const prevColorSystem = prevFilters.find((i) => i.type === "color")?.value
        ?.colorSystem;
      const prevIsColorSystem = prevFilters.find(
        (i) => i.type === "isColorSystem",
      );

      // Only auto-tick when:
      // 1. Switching to a supported color system for the first time (no previous isColorSystem filter)
      // 2. Switching between different supported color systems (update the value)
      if (
        selectedColorSystem &&
        supportedColorSystems.includes(selectedColorSystem)
      ) {
        // Check if this is a color system change (not just a re-render)
        const isColorSystemChange = prevColorSystem !== selectedColorSystem;

        if (isColorSystemIndex === -1 && isColorSystemChange) {
          // Auto-add only when switching to a supported system for the first time
          newFilters.push({
            type: "isColorSystem",
            value: selectedColorSystem,
          });
        } else if (
          isColorSystemIndex !== -1 &&
          isColorSystemChange &&
          prevIsColorSystem
        ) {
          // Update when switching between supported systems, but only if it was previously set
          newFilters[isColorSystemIndex].value = selectedColorSystem;
        }
        // If isColorSystemIndex === -1 and it's not a change, user has unchecked it - don't re-add
      } else if (
        selectedColorSystem &&
        !supportedColorSystems.includes(selectedColorSystem)
      ) {
        // Remove isColorSystem filter when switching to an unsupported color system
        if (isColorSystemIndex !== -1) {
          newFilters.splice(isColorSystemIndex, 1);
        }
      }

      const isColorSystem = newFilters.find((i) => i.type === "isColorSystem");
      let codeValue: string | undefined;
      if (isColorSystem?.value === "ncs" && filterColor?.value.color?.name) {
        codeValue = filterColor.value.color.name;
      } else if (filterColor?.value.color?.code) {
        codeValue = filterColor.value.color.code;
      }

      if (codeValue) {
        if (colorSystemCodeIndex !== -1) {
          newFilters[colorSystemCodeIndex].value = codeValue;
        } else {
          newFilters.push({
            type: "colorSystemCode",
            value: codeValue,
          });
        }
      } else {
        newFilters = newFilters.filter((i) => i.type !== "colorSystemCode");
      }

      return newFilters;
    });
  };

  if (landingPageLoading) {
    return <FullScreenLoader />;
  }
  const isDemo = userInfo?.accessLevel === "DEMO";
  const homePageContainer = (
    <Box
      sx={{
        maxWidth: "1750px",
        margin: "0 auto",
        overflowX: "hidden",
        px: { xs: "16px", sm: 0 },
      }}
    >
      <SynchronizedCarousels
        bannerPageCard={bannerPageCard}
        ldPageCard={ldPageCard}
        isLogin={isLogin}
        t={t}
      />
      {showConventionalSearch && (
        <Grid
          container
          spacing={3}
          sx={{
            backgroundColor: "var(--global-neutral-white, #fff)",
            marginTop: 0,
            marginBottom: 0,
            paddingTop: "30px",
            paddingBottom: "32px",
            paddingLeft: { xs: "16px", sm: "32px", md: "64px", lg: "96px" },
            paddingRight: { xs: "16px", sm: "32px", md: "64px", lg: "96px" },
            "& .MuiPaper-root": {
              backgroundColor: "var(--global-neutral-white, #fff)",
            },
          }}
        >
          <Grid size={12}>
            <SearchFilter
              key={"LandingPageSearchFilter"}
              data-cy={"LandingPageSearchFilter"}
              columns={columns}
              childColumns={childColumns}
              value={filters}
              onChange={(newFilters) => setAndSaveFilters(newFilters)}
              canMinimize={false}
              isLandingPage={true}
            ></SearchFilter>
            <SearchButtonAuthen
              isLogin={isLogin}
              error={error}
              result={result}
              isLoading={isLoading}
              filters={filters}
            />
          </Grid>
        </Grid>
      )}
      {isLogin && !isDemo && (
        <Grid
          sx={{
            backgroundColor: "#fff",
            textAlign: "center",
            justifyItems: "center",
            minHeight: "144px",
            padding: "30px 16px",
            marginTop: "42px",
          }}
        >
          <ContactRequestButton t={t} />
        </Grid>
      )}
    </Box>
  );
  const showSearch = !showResultsOnly && showConventionalSearch;
  const homePageContainerV2 = (
    <Box>
      {!showResultsOnly && flags.homepageV2.showCovBannerCarousel && (
        <>
          <CovBannerCarousel
            banners={banners}
            autoSlide
            slideIntervalMs={5000}
            animationDurationMs={500}
            onNavigate={handleNavigate}
          />
          <UnauthenticationDialog
            isOpen={openAuthDialog}
            onClose={setOpenAuthDialog}
          />
        </>
      )}
      {!showResultsOnly && flags.homepageV2.showLegacyCarousel && (
        <SynchronizedCarousels
          bannerPageCard={bannerPageCard}
          ldPageCard={ldPageCard}
          isLogin={isLogin}
          t={t}
          hideCardsCarousel
        />
      )}
      <DiscoveryNavOutlet />
      <DiscoverySection
        isLogin={isLogin}
        onFlowStepChange={handleFlowStepChange}
        enableListView={uploadMatchEnabled}
        defaultToEffect={!showConventionalSearch}
        initialTabId={requestedTab}
        initialImageFile={handoffCleared ? initialImageFile : null}
        inlinePanel={!showConventionalSearch}
      />
      {showSearch && (
        <Grid
          container
          spacing={3}
          sx={{
            backgroundColor: "var(--global-neutral-white, #fff)",
            marginTop: 0,
            marginBottom: 0,
            paddingTop: "30px",
            paddingBottom: "32px",
            paddingLeft: { xs: "16px", sm: "32px", md: "64px", lg: "96px" },
            paddingRight: { xs: "16px", sm: "32px", md: "64px", lg: "96px" },
            "& .MuiPaper-root": {
              backgroundColor: "var(--global-neutral-white, #fff)",
            },
          }}
        >
          <Grid size={12}>
            <Box
              sx={{
                "& .MuiInputBase-root": {
                  height: 46,
                  backgroundColor: "var(--global-neutral-white, #fff)",
                  "&.Mui-disabled": {
                    backgroundColor: "var(--alias-surface-low, #f5f5f5)",
                  },
                },
                "& .MuiInputLabel-root": {
                  top: -4,
                  "&.MuiInputLabel-shrink": { top: 0 },
                },
                "& .MuiListItem-root": {
                  backgroundColor: "var(--global-neutral-white, #fff)",
                },
              }}
            >
              <SearchFilter
                key={"LandingPageSearchFilterV2"}
                data-cy={"LandingPageSearchFilterV2"}
                columns={columns}
                childColumns={childColumns}
                value={filters}
                onChange={(newFilters) => setAndSaveFilters(newFilters)}
                canMinimize={false}
                isLandingPage={true}
              />
            </Box>
            <Box sx={{ "& > div": { backgroundColor: "transparent" } }}>
              <SearchButtonAuthen
                isLogin={isLogin}
                error={error}
                result={result}
                isLoading={isLoading}
                filters={filters}
              />
            </Box>
          </Grid>
        </Grid>
      )}
      {!showResultsOnly && (
        <>
          <PromotionalCTA isLogin={isLogin} />
          <MaterialShowcase isLogin={isLogin} />
          {flags.homepageV2.showTrendCarousel && (
            <TrendCarousel items={trendItems} />
          )}
        </>
      )}
      {!showResultsOnly && isLogin && !isDemo && (
        <Grid
          sx={{
            backgroundColor: "#fff",
            textAlign: "center",
            justifyItems: "center",
            minHeight: "144px",
            padding: "30px 16px",
          }}
        >
          <ContactRequestButton t={t} />
        </Grid>
      )}
    </Box>
  );
  return isHomepageV2 ? homePageContainerV2 : homePageContainer;
};
